import { useEffect, useRef, RefObject, useState, useCallback } from 'react';
import { useStore } from 'react-redux';
import {
  ACTION_AUTH_SESSION_TOKEN,
  ACTION_STORE_INFO,
  ACTION_READY,
  ACTION_CONNECTED,
  ACTION_NAVIGATE_BACK,
  ACTION_NAVIGATE_GOTO,
  ACTION_UTILS_COPY_TO_CLIPBOARD,
  ACTION_NAVIGATE_PATHNAME,
  ACTION_NAVIGATE_SYNC,
  ACTION_NAVIGATE_EXIT,
  ACTION_NAVIGATE_GOTO_OLD_ADMIN,
  ACTION_NAVIGATE_HEADER,
  ACTION_DEVICE,
  ACTION_LOG_ERROR,
  NavigateGoToOldAdminRequest,
  NavigateSyncRequest,
  NavigateGoToRequest,
  NavigateHeaderRequest,
  CopyToClipboardRequest,
  ACTION_UTILS_OPEN_IN_DEFAULT_BROWSER,
  ACTION_USER_ADMIN_INFO,
  ACTION_STORE_FEATURE,
  ACTION_STORE_UPSELL,
  StoreUpsellRequest,
  StoreFeatureRequest,
} from '@tiendanube/nexo';
import useTopBar from 'App/components/Topbar/useTopBar';
import { useClipboard, useNavegate, useSubPathname } from 'App/hooks';
import { RootStateType } from 'App/store';
import goToAdmin from 'commons/utils/gotToAdmin';
import { openWindow } from 'commons/utils/window';
import { getStatusFeatureByKey } from 'domains/Auth/authSlice/authSelectors';
import { useIsMobileDevice } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { UpsellResultEnum } from 'domains/Billing/UpsellFlow/hooks/useUpsellFlow';
import {
  useGetToken,
  useStatusEmbeddedApp,
  useStoreInfo,
  useUserAdminInfo,
} from 'domains/PartnersApps/hooks';
import { createNexo, NexoInstance } from 'libs/nexo/nexo';
import { digestStoreInfo } from './digestStoreInfo';

export const DEFAULT_UPSELL_TRACKING_SOURCE = 'embedded_app';

function useNexoClient(
  appId: string,
  iframeRef: RefObject<HTMLIFrameElement>,
  windowRef: RefObject<Window | null>,
) {
  const navegate = useNavegate();
  const subPathname = useSubPathname();
  const statusApp = useStatusEmbeddedApp();
  const { setHeaderNavigation } = useTopBar();
  const copyToClipboard = useClipboard();
  const storeInfo = useStoreInfo();
  const userAdminInfo = useUserAdminInfo();
  const getToken = useGetToken();
  const isMobileDevice = useIsMobileDevice();
  const nexoRef = useRef({} as NexoInstance);
  const store = useStore<RootStateType>();
  const [upsellRequest, setUpsellRequest] = useState<StoreUpsellRequest | null>(
    null,
  );

  const handleUpsellSuccessOrClose = useCallback(
    (upsellResult: UpsellResultEnum) => {
      if (nexoRef.current.dispatch) {
        nexoRef.current.dispatch(
          {
            type: ACTION_STORE_UPSELL,
            payload: { result: upsellResult },
          },
          windowRef?.current,
        );
      }
      setUpsellRequest(null);
    },
    [windowRef],
  );

  const handleUpsellPlansRedirect = useCallback(() => {
    if (nexoRef.current.dispatch) {
      nexoRef.current.dispatch(
        {
          type: ACTION_STORE_UPSELL,
          payload: { result: UpsellResultEnum.PLANS_REDIRECT },
        },
        windowRef?.current,
      );
    }
    setUpsellRequest(null);
  }, [windowRef]);

  const triggerUpsell = useUpsellFlow({
    title: upsellRequest?.modalTitle || '',
    featureKey: upsellRequest?.featureKey || '',
    trackingSource:
      upsellRequest?.trackingSource || DEFAULT_UPSELL_TRACKING_SOURCE,
    callback: handleUpsellSuccessOrClose,
    onCloseModal: handleUpsellSuccessOrClose,
    onPlansRedirect: handleUpsellPlansRedirect,
    includeCallbackResult: true,
  });

  // Trigger the upsell flow when upsellRequest changes
  useEffect(() => {
    if (upsellRequest?.featureKey) {
      triggerUpsell();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsellRequest]);

  useEffect(() => {
    if (nexoRef.current.dispatch) {
      nexoRef.current.dispatch(
        {
          type: ACTION_NAVIGATE_SYNC,
          payload: { path: subPathname.value ?? '/', replace: false },
        },
        windowRef?.current,
      );
    }
  }, [subPathname.value, windowRef]);

  useEffect(() => {
    if (!iframeRef.current) return;
    nexoRef.current = createNexo(iframeRef.current);
    const nexo = nexoRef.current;

    /**
     * LISTENER ACTION CONNECTED
     * */
    nexo.suscribe(ACTION_CONNECTED, () => {
      nexo.dispatch({ type: ACTION_CONNECTED }, windowRef?.current);
    });

    /**
     * LISTENER ACTION READY
     * */
    nexo.suscribe(ACTION_READY, () => {
      statusApp.handleReady();
      const subPath = subPathname.value || subPathname.restoreTemporal();
      if (subPath) {
        nexo.dispatch(
          {
            type: ACTION_NAVIGATE_SYNC,
            payload: { path: subPath, replace: true },
          },
          windowRef?.current,
        );
        //If there is a persisted subPathname it will be restored
        subPathname.restoreTemporal();
      }
    });

    /**
     * LISTENER ACTION READY
     * */
    /**
     * This flag is necessary to avoid the first sync with the root
     * path that is causing a desynchronization with the parent path
     */
    let toAvoidUnnecessaryFirstSyncRoot = true;
    nexo.suscribe(ACTION_NAVIGATE_SYNC, ({ pathname }: NavigateSyncRequest) => {
      if (!toAvoidUnnecessaryFirstSyncRoot) {
        subPathname.syncNewSubPathname(pathname);
      }
      toAvoidUnnecessaryFirstSyncRoot = false;
    });

    /**
     * LISTENER ACTION EXIT
     * */
    nexo.suscribe(ACTION_NAVIGATE_EXIT, () => {
      navegate.goToReferrer();
    });

    /**
     * LISTENER ACTION GO TO OLD ADMIN
     * */
    nexo.suscribe(
      ACTION_NAVIGATE_GOTO_OLD_ADMIN,
      ({ pathToOldAdmin }: NavigateGoToOldAdminRequest) => {
        goToAdmin(pathToOldAdmin)();
      },
    );

    /**
     * LISTENER ACTION NAVEGATE BACK
     * */
    nexo.suscribe(ACTION_NAVIGATE_BACK, () => {
      navegate.goBack();
    });

    /**
     * LISTENER ACTION NAVEGATE GO TO
     * */
    nexo.suscribe(ACTION_NAVIGATE_GOTO, ({ pathname }: NavigateGoToRequest) => {
      navegate.goTo(pathname);
    });

    /**
     * LISTENER ACTION NAVEGATE SUBPATHNAME
     * */
    nexo.suscribe(ACTION_NAVIGATE_PATHNAME, () => {
      nexo.dispatch(
        {
          type: ACTION_NAVIGATE_PATHNAME,
          payload: { pathname: subPathname.value },
        },
        windowRef?.current,
      );
    });

    /**
     * LISTENER ACTION AUTH_SESSION_TOKEN
     * */
    nexo.suscribe(ACTION_AUTH_SESSION_TOKEN, async () => {
      const token = await getToken(appId);
      nexo.dispatch(
        {
          type: ACTION_AUTH_SESSION_TOKEN,
          payload: { token },
        },
        windowRef?.current,
      );
    });

    /**
     * LISTENER ACTION STORE INFO
     * */
    nexo.suscribe(ACTION_STORE_INFO, () => {
      nexo.dispatch(
        { type: ACTION_STORE_INFO, payload: digestStoreInfo(storeInfo) },
        windowRef?.current,
      );
    });

    /**
     * LISTENER ACTION USER ADMIN INFO
     */
    nexo.suscribe(ACTION_USER_ADMIN_INFO, () => {
      nexo.dispatch(
        {
          type: ACTION_USER_ADMIN_INFO,
          payload: userAdminInfo,
        },
        windowRef?.current,
      );
    });

    /**
     * LISTENER ACTION IS MOBILE DEVICE
     * */
    nexo.suscribe(ACTION_DEVICE, () => {
      nexo.dispatch(
        {
          type: ACTION_DEVICE,
          payload: { isMobile: isMobileDevice },
        },
        windowRef?.current,
      );
    });

    /**
     * LISTENER ACTION UTIL COPY TO CLIPBOARD
     * */
    nexo.suscribe(
      ACTION_UTILS_COPY_TO_CLIPBOARD,
      async ({ text }: CopyToClipboardRequest) => {
        const success = await copyToClipboard(text);
        nexo.dispatch(
          {
            type: ACTION_UTILS_COPY_TO_CLIPBOARD,
            payload: { success },
          },
          windowRef?.current,
        );
      },
    );

    /**
     * LISTENER ACTION UTIL OPEN IN DEFAULT BROWSER
     * */
    nexo.suscribe(
      ACTION_UTILS_OPEN_IN_DEFAULT_BROWSER,
      async (path: { pathname: string }) => {
        openWindow(path.pathname, true);
      },
    );

    /**
     * LISTENER ACTION NAVEGATE HEADER
     * */
    nexo.suscribe(
      ACTION_NAVIGATE_HEADER,
      ({ goTo, goToAdmin, text, remove }: NavigateHeaderRequest) => {
        if (remove) {
          setHeaderNavigation(undefined);
          return;
        }

        const isGoToBack = goTo === 'back';
        const isGoToPath = goTo?.indexOf('/') === 0;
        const isGoToAdminPath = goToAdmin?.indexOf('/') === 0;

        if (!isGoToBack && !isGoToPath && !isGoToAdminPath) {
          //'Nexo: the goTo string is invalid try to define `back` or an path starting with `/`
          return;
        }

        const goToInApp = () => {
          nexo.dispatch(
            {
              type: ACTION_NAVIGATE_SYNC,
              payload: { path: goTo },
            },
            windowRef?.current,
          );
          navegate.goTo(`${navegate.pathname}#${goTo}`);
        };

        const goToInAdmin = () => navegate.goTo(`${goToAdmin}`);

        setHeaderNavigation({
          onClick: isGoToBack
            ? navegate.goBack
            : isGoToPath
            ? goToInApp
            : goToInAdmin,
          children: text,
        });
      },
    );

    /**
     * LISTENER ACTION LOG ERROR
     * */
    nexo.suscribe(ACTION_LOG_ERROR, () => {
      statusApp.handleError();
    });

    /**
     * LISTENER ACTION GET FEATURE STATUS FOR STORE
     */
    nexo.suscribe(ACTION_STORE_FEATURE, (request: StoreFeatureRequest) => {
      const currentState = store.getState();
      const featureStatus = getStatusFeatureByKey(
        currentState,
        request.featureKey,
      );

      nexo.dispatch(
        {
          type: ACTION_STORE_FEATURE,
          payload: {
            isFeatureEnabled: featureStatus.isAvailable,
            unlimited: featureStatus.unlimited,
            maxUse: featureStatus.maxUse,
          },
        },
        windowRef?.current,
      );
    });

    /**
     * LISTENER ACTION RUN UPSELL FLOW
     */
    nexo.suscribe(ACTION_STORE_UPSELL, (request: StoreUpsellRequest) => {
      setUpsellRequest(request);
    });

    return () => {
      nexo.disconnect();
      nexoRef.current = {} as NexoInstance;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeRef.current, appId]);

  return statusApp;
}

export default useNexoClient;
